import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Search, Bell, User, LogOut, Settings, Menu } from 'lucide-react';
import { theme, media } from '../../styles/theme';

const HeaderContainer = styled.header`
  height: 70px;
  background: ${theme.colors.white};
  border-bottom: 1px solid ${theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${theme.spacing.xl};
  position: sticky;
  top: 0;
  z-index: ${theme.zIndex.sticky};
  box-shadow: ${theme.shadows.sm};
  margin-left: 260px;
  transition: margin-left ${theme.transitions.duration.slow} ${theme.transitions.easing.easeOut};

  ${media.down.lg} {
    margin-left: 0;
    padding: 0 ${theme.spacing.md};
  }

  ${media.down.sm} {
    height: 60px;
    padding: 0 ${theme.spacing.sm};
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.lg};
  flex: 1;

  ${media.down.sm} {
    gap: ${theme.spacing.md};
  }
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  max-width: 500px;

  ${media.down.md} {
    max-width: 300px;
  }

  ${media.down.sm} {
    display: none;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${theme.spacing.sm} ${theme.spacing.md} ${theme.spacing.sm} 40px;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.base};
  font-family: ${theme.typography.fontFamily};
  background: ${theme.colors.secondary};
  transition: all ${theme.transitions.duration.base};
  outline: none;

  &:focus {
    border-color: ${theme.colors.primary};
    background: ${theme.colors.white};
    box-shadow: 0 0 0 3px ${theme.colors.primary}15;
  }

  &::placeholder {
    color: ${theme.colors.textLight};
  }
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: ${theme.spacing.md};
  top: 50%;
  transform: translateY(-50%);
  color: ${theme.colors.textLight};
  width: 18px;
  height: 18px;
  pointer-events: none;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};

  ${media.down.sm} {
    gap: ${theme.spacing.sm};
  }
`;

const IconButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: ${theme.borderRadius.md};
  border: none;
  background: ${theme.colors.secondary};
  color: ${theme.colors.text};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ${theme.transitions.duration.base};
  position: relative;

  &:hover {
    background: ${theme.colors.primary}15;
    color: ${theme.colors.primary};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    width: 20px;
    height: 20px;
  }

  ${media.down.sm} {
    width: 36px;
    height: 36px;

    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

const NotificationBadge = styled.span`
  position: absolute;
  top: -2px;
  right: -2px;
  width: 18px;
  height: 18px;
  border-radius: ${theme.borderRadius.full};
  background: linear-gradient(135deg, ${theme.colors.error} 0%, #E53935 100%);
  color: ${theme.colors.white};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.bold};
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid ${theme.colors.white};
`;

const UserMenu = styled.div`
  position: relative;
`;

const UserButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.xs} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border};
  background: ${theme.colors.white};
  cursor: pointer;
  transition: all ${theme.transitions.duration.base};

  &:hover {
    border-color: ${theme.colors.primary};
    background: ${theme.colors.primary}05;
    box-shadow: ${theme.shadows.sm};
  }

  ${media.down.sm} {
    padding: ${theme.spacing.xs};
    gap: ${theme.spacing.xs};
  }
`;

const UserAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: ${theme.borderRadius.full};
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryLight} 100%);
  color: ${theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${theme.typography.fontWeight.bold};
  font-size: ${theme.typography.fontSize.base};
  flex-shrink: 0;

  ${media.down.sm} {
    width: 32px;
    height: 32px;
    font-size: ${theme.typography.fontSize.sm};
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;

  ${media.down.sm} {
    display: none;
  }
`;

const UserName = styled.span`
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.semiBold};
  color: ${theme.colors.text};
  line-height: 1.2;
`;

const UserRole = styled.span`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.textSecondary};
  line-height: 1.2;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + ${theme.spacing.sm});
  right: 0;
  background: ${theme.colors.white};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.xl};
  border: 1px solid ${theme.colors.border};
  min-width: 200px;
  overflow: hidden;
  opacity: ${({ isOpen }) => isOpen ? 1 : 0};
  visibility: ${({ isOpen }) => isOpen ? 'visible' : 'hidden'};
  transform: ${({ isOpen }) => isOpen ? 'translateY(0)' : 'translateY(-10px)'};
  transition: all ${theme.transitions.duration.base};
  z-index: ${theme.zIndex.dropdown};
`;

const DropdownItem = styled.button`
  width: 100%;
  padding: ${theme.spacing.md};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  border: none;
  background: ${theme.colors.white};
  color: ${theme.colors.text};
  cursor: pointer;
  font-size: ${theme.typography.fontSize.base};
  font-family: ${theme.typography.fontFamily};
  text-align: left;
  transition: background ${theme.transitions.duration.fast};

  &:hover {
    background: ${theme.colors.secondary};
  }

  svg {
    width: 18px;
    height: 18px;
    color: ${theme.colors.textSecondary};
  }

  &:last-child {
    border-top: 1px solid ${theme.colors.border};
    color: ${theme.colors.error};

    svg {
      color: ${theme.colors.error};
    }

    &:hover {
      background: ${theme.colors.error}10;
    }
  }
`;

const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textSecondary};

  ${media.down.md} {
    display: none;
  }

  span {
    color: ${theme.colors.textLight};
  }

  a {
    color: ${theme.colors.textSecondary};
    text-decoration: none;
    transition: color ${theme.transitions.duration.fast};

    &:hover {
      color: ${theme.colors.primary};
    }
  }

  .current {
    color: ${theme.colors.text};
    font-weight: ${theme.typography.fontWeight.semiBold};
  }
`;

function Header({ user = { name: 'Admin User', role: 'Administrator' }, onMenuClick }) {
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [notificationCount] = useState(3); // Mock notification count
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  const handleLogout = () => {
    setShowUserMenu(false);
    navigate('/login');
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const getCurrentPath = () => {
    const path = window.location.pathname;
    const parts = path.split('/').filter(Boolean);
    
    if (parts.length === 0) return 'Dashboard';
    
    const lastPart = parts[parts.length - 1];
    return lastPart.charAt(0).toUpperCase() + lastPart.slice(1).replace(/-/g, ' ');
  };

  return (
    <HeaderContainer>
      <LeftSection>
        {onMenuClick && (
          <IconButton onClick={onMenuClick} aria-label="Menu">
            <Menu />
          </IconButton>
        )}
        <Breadcrumb>
          <a href="/admin/dashboard">Home</a>
          <span>/</span>
          <span className="current">{getCurrentPath()}</span>
        </Breadcrumb>
        <SearchContainer>
          <SearchIcon />
          <SearchInput 
            type="text" 
            placeholder="Search orders, items, customers..." 
          />
        </SearchContainer>
      </LeftSection>

      <RightSection>
        <IconButton aria-label="Notifications" style={{ position: 'relative' }}>
          <Bell />
          {notificationCount > 0 && (
            <NotificationBadge>{notificationCount}</NotificationBadge>
          )}
        </IconButton>

        <UserMenu ref={menuRef}>
          <UserButton onClick={() => setShowUserMenu(!showUserMenu)}>
            <UserAvatar>{getInitials(user.name)}</UserAvatar>
            <UserInfo>
              <UserName>{user.name}</UserName>
              <UserRole>{user.role}</UserRole>
            </UserInfo>
          </UserButton>

          <DropdownMenu isOpen={showUserMenu}>
            <DropdownItem onClick={() => {
              setShowUserMenu(false);
              navigate('/admin/settings');
            }}>
              <Settings />
              Settings
            </DropdownItem>
            <DropdownItem onClick={() => {
              setShowUserMenu(false);
              navigate('/admin/staff');
            }}>
              <User />
              Profile
            </DropdownItem>
            <DropdownItem onClick={handleLogout}>
              <LogOut />
              Logout
            </DropdownItem>
          </DropdownMenu>
        </UserMenu>
      </RightSection>
    </HeaderContainer>
  );
}

export default Header;
